import { getCatalogProviderManifest } from "@repo/shared";
import { Database } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

function humanizeProviderKey(providerKey: string) {
  return providerKey
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
}

export function getCatalogProviderDisplayName(providerKey: string) {
  return (
    getCatalogProviderManifest(providerKey as never)?.displayName ??
    humanizeProviderKey(providerKey)
  );
}

function PurviewLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="none">
      <path
        d="M5.5 6.5 12 3l6.5 3.5v10L12 20.5 5.5 16.5z"
        fill="url(#purviewGradient)"
        opacity="0.18"
      />
      <path d="M5.5 6.5 12 3l6.5 3.5v10L12 20.5 5.5 16.5z" stroke="#0F766E" strokeWidth="1.4" />
      <path d="M9 9.5h6M9 12h6M9 14.5h4" stroke="#0F766E" strokeLinecap="round" strokeWidth="1.5" />
      <defs>
        <linearGradient
          id="purviewGradient"
          x1="5.5"
          x2="18.5"
          y1="3"
          y2="20.5"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#14B8A6" />
          <stop offset="1" stopColor="#0EA5E9" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function DatabricksLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="none">
      <path d="M4.5 7.2 12 3l7.5 4.2L12 11.4z" fill="#EF4444" />
      <path d="M4.5 12.2 12 8l7.5 4.2L12 16.4z" fill="#DC2626" />
      <path d="M4.5 17.2 12 13l7.5 4.2L12 21.4z" fill="#B91C1C" />
    </svg>
  );
}

function GenericCatalogLogo({ className }: { className?: string }) {
  return <Database className={className} aria-hidden="true" />;
}

export function CatalogProviderIcon({
  providerKey,
  className,
}: {
  providerKey: string;
  className?: string;
}) {
  if (providerKey === "purview") {
    return <PurviewLogo className={className} />;
  }
  if (providerKey === "unityCatalog") {
    return <DatabricksLogo className={className} />;
  }
  return <GenericCatalogLogo className={className} />;
}

export function CatalogProviderInlineMark({
  providerKey,
  label,
  className,
}: {
  providerKey: string;
  label?: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-sm border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium tracking-wide text-muted-foreground",
        className
      )}
    >
      <CatalogProviderIcon providerKey={providerKey} className="size-3 shrink-0" />
      {label ? <span className="truncate">{label}</span> : null}
    </span>
  );
}
